/**
 * useVpr Hook
 *
 * Custom React hook for managing VPR (Visual Place Recognition) data.
 * Follows the same pattern as hooks/useRoomRecognition.js with added index management.
 */

import { useState, useCallback, useEffect } from 'react';
import {
  addRoom,
  deleteRoom,
  getVenueRooms,
  buildIndex,
  getIndexStatus,
  testMatch
} from '../VPR/utils/vprDatabase';

/**
 * Hook for managing VPR for a specific museum/guide
 *
 * @param {string} familyId - Shared ID across language versions
 * @param {string} guideId - Current language-specific guide ID
 * @param {string} museumName - Museum name (for display)
 */
export const useVpr = (familyId, guideId, museumName = '') => {
  // State
  const [rooms, setRooms] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [indexStatus, setIndexStatus] = useState(null);
  const [isBuilding, setIsBuilding] = useState(false);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Clear test result
  const clearTestResult = useCallback(() => {
    setTestResult(null);
  }, []);

  // ============================================================================
  // FETCH OPERATIONS
  // ============================================================================

  /**
   * Fetch all rooms for the current venue
   */
  const fetchRooms = useCallback(async () => {
    if (!familyId) {
      setRooms([]);
      return [];
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log('[useVpr] Fetching rooms for familyId:', familyId);
      const fetchedRooms = await getVenueRooms(familyId);
      console.log('[useVpr] Fetched rooms:', fetchedRooms.length);
      setRooms(fetchedRooms);
      return fetchedRooms;
    } catch (err) {
      console.error('[useVpr] Error fetching rooms:', err);
      setError(err.message || 'Failed to fetch rooms');
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [familyId]);

  /**
   * Fetch index status for the current venue
   */
  const fetchIndexStatus = useCallback(async () => {
    if (!familyId) {
      setIndexStatus(null);
      return null;
    }

    try {
      console.log('[useVpr] Fetching V2 status for familyId:', familyId);
      const status = await getIndexStatus(familyId);
      setIndexStatus(status);
      return status;
    } catch (err) {
      console.error('[useVpr] Error fetching index status:', err);
      // Don't set error for index status - it's not critical
      return null;
    }
  }, [familyId]);

  // ============================================================================
  // CRUD OPERATIONS
  // ============================================================================

  /**
   * Create a new room with reference images
   */
  const createRoom = useCallback(async (roomData, imageFiles, metadata = {}) => {
    if (!familyId || !guideId) {
      throw new Error('Guide family ID and guide ID are required');
    }

    setIsSaving(true);
    setError(null);

    try {
      const roomId = roomData.roomId || `${familyId}_v2_${Date.now()}`;

      const result = await addRoom(
        {
          roomId,
          familyId,
          guideId,
          museumName: museumName || roomData.venueName || familyId,
          roomName: roomData.roomName,
          stopId: roomData.stopId
        },
        imageFiles,
        metadata
      );

      // Refresh rooms list and index status
      await fetchRooms();
      await fetchIndexStatus();

      return result;
    } catch (err) {
      console.error('[useVpr] Error creating room:', err);
      setError(err.message || 'Failed to create room');
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [familyId, guideId, museumName, fetchRooms, fetchIndexStatus]);

  /**
   * Delete a room
   */
  const removeRoom = useCallback(async (roomId) => {
    setIsSaving(true);
    setError(null);

    try {
      await deleteRoom(roomId, familyId);

      // Update local state
      setRooms(prev => prev.filter(r => r.roomId !== roomId));

      // Refresh index status (index may be stale now)
      await fetchIndexStatus();

      return { success: true };
    } catch (err) {
      console.error('[useVpr] Error deleting room:', err);
      setError(err.message || 'Failed to delete room');
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [familyId, fetchIndexStatus]);

  // ============================================================================
  // INDEX OPERATIONS
  // ============================================================================

  /**
   * Build or rebuild the CLIP index
   */
  const buildVprIndex = useCallback(async () => {
    if (!familyId) {
      throw new Error('Guide family ID is required');
    }

    setIsBuilding(true);
    setError(null);

    try {
      console.log('[useVpr] Publishing V2 for familyId:', familyId);
      const result = await buildIndex(familyId);

      // Refresh index status after build
      await fetchIndexStatus();

      return result;
    } catch (err) {
      console.error('[useVpr] Error building index:', err);
      setError(err.message || 'Failed to build index');
      throw err;
    } finally {
      setIsBuilding(false);
    }
  }, [familyId, fetchIndexStatus]);

  // ============================================================================
  // TESTING OPERATIONS
  // ============================================================================

  /**
   * Test VPR matching with an image
   */
  const testVprMatch = useCallback(async (imageFile) => {
    setIsLoading(true);
    setError(null);
    setTestResult(null);

    try {
      const result = await testMatch(imageFile, familyId, guideId);
      setTestResult(result);
      return result;
    } catch (err) {
      console.error('[useVpr] Test match error:', err);
      setError(err.message || 'VPR test failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [familyId, guideId]);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Fetch rooms and index status when venue changes
  useEffect(() => {
    if (familyId) {
      fetchRooms();
      fetchIndexStatus();
    } else {
      setRooms([]);
      setIndexStatus(null);
    }
  }, [familyId, fetchRooms, fetchIndexStatus]);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  const roomCount = rooms.length;
  const hasRooms = roomCount > 0;
  const hasIndex = !!(indexStatus && indexStatus.published);
  const indexNeedsRebuild = hasRooms && !hasIndex;

  // ============================================================================
  // RETURN
  // ============================================================================

  return {
    // State
    rooms,
    isLoading,
    isSaving,
    error,
    testResult,
    indexStatus,
    isBuilding,

    // Computed
    roomCount,
    hasRooms,
    hasIndex,
    indexNeedsRebuild,

    // Actions
    clearError,
    clearTestResult,

    // Fetch operations
    fetchRooms,
    fetchIndexStatus,

    // CRUD operations
    createRoom,
    removeRoom,

    // Index operations
    buildVprIndex,

    // Testing
    testVprMatch
  };
};

export default useVpr;
